import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { GlobalInviteWithLazyLoadedRelayProvider } from '@atlassian/global-invite';
import { MenuList } from '@atlassian/navigation-system/side-nav/menu-list';
import { PanelSplitter } from '@atlassian/navigation-system/layout/panel-splitter';
import {
	SideNav,
	useToggleSideNav,
	SideNavFooter,
	useExpandSideNav,
} from '@atlassian/navigation-system/layout/side-nav';

import {
	Attribution,
	ErrorDisplay,
	isUnauthorizedError,
	TransparentErrorBoundary,
} from '@confluence/error-boundary';
import { type WithFlagsProps, withFlags } from '@confluence/flags';
import { useSettingsBackButtonUtils } from '@confluence/admin-side-navigation';
import {
	confluenceLocalStorageInstance,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import {
	AdminNavigationLoader,
	SpaceAdminNavigation,
	SpaceNavigation,
	SpaceNavigationLoadingSkeleton,
	GlobalNavigation,
	HubSettingsNavigation,
	StarredSpacesList,
} from '@confluence/side-navigation';
import { isDialogShown } from '@confluence/dialogs';
import {
	GeneralShortcutListener,
	ShortcutListener,
	SIDE_NAVIGATION_SHORTCUT,
	UNIVERSAL_SIDE_NAVIGATION_SHORTCUT,
} from '@confluence/shortcuts';
import { RoutesContext } from '@confluence/route-manager';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { usePageSpaceKey } from '@confluence/page-context';
import { WHITEBOARD_IFRAME_TITLE } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants';
import {
	CoordinatedNav4OnboardingModal,
	type Nav4ChangeboardingSpotlightProps,
} from '@confluence/nav4-onboarding';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { AnonymousAccessSideNavMessage } from '@confluence/anonymous-access/entry-points/AnonymousAccessSideNavMessage';
import { useIsUserViewingSpaceAsAnon } from '@confluence/anonymous-access/entry-points/useIsUserViewingSpaceAsAnon';
import { usePersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/usePersistentInviteButtonEligible';
import { markErrorAsHandled } from '@confluence/graphql-error-processor';
import { useSessionData } from '@confluence/session-data';
import { PostOfficeConfluenceSideNavPlacement } from '@confluence/experiment-post-office-side-nav';
import { useIsEligibleForEnhanceOrientationTour } from '@confluence/experiment-enhance-orientation-tour/entry-points/useIsEligibleForEnhanceOrientationTour';
import { EnhanceOrientationTourNudgeLoader } from '@confluence/experiment-enhance-orientation-tour/entry-points/EnhanceOrientationTourNudge';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools';
import { useModernizeChangeboardingState } from '@confluence/modernize-changeboarding/entry-points/useModernizeChangeboardingState';
import { useGetModernizeChangeboardingOnboardingState } from '@confluence/modernize-changeboarding/entry-points/useGetModernizeChangeboardingOnboardingState';
import { RtPaidFeatureEducationComponentLoadable } from '@confluence/experiment-reverse-trial-paid-feature-education/entry-points/RtPaidFeatureEducationComponent';
import {
	Nav4CollapseReminderSpotlightLoader,
	useHasDismissedNav4CollapseReminderSpotlight,
} from '@confluence/nav4-onboarding/entry-points/Nav4CollapseReminderSpotlightLoader';

import { SideNavigationContent } from './SideNavigationContent';
import { useSideNavInitialState } from './useSideNavInitialState';
import { useScrollTree } from './useScrollTree';
import {
	ADMIN_HOME_ID,
	CONTAINER_ADMIN_HOME_ID,
	CONTAINER_BLOG_ID,
	CONTAINER_HOME_ID,
	COMPANY_HUB_SETTINGS_ID,
	CONTAINER_HOME_LOADING_ID,
	PRODUCT_HOME_ID,
} from './paths';
import { Nav4GiveFeedbackButton } from './Nav4GiveFeedbackButton';
import { useSideNavigationCollapsedActions } from './SideNavigationCollapsedStore';

const i18n = defineMessages({
	dragHandle: {
		id: 'page-layout.side-navigation.drag-handle',
		description:
			'A label to tell visually impaired users that the selected element is the side navigation drag handle for resizing the sidebar',
		defaultMessage: 'Side Navigation Drag Handle',
	},
	globalInviteButtonLabel: {
		id: 'page-layout.side-navigation.global-invite-button-label',
		description: 'Text content of the button that triggers the modal dialog.',
		defaultMessage: 'Invite people',
	},
});
const resize = () => {
	window.dispatchEvent(new Event('resize'));
};
const ExpandKeyboardShortcut = ({ onToggleFromShortcut }) => {
	const isOnEditRoute = useIsEditorPage();
	const toggleSideNav = useToggleSideNav();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const toggle = useCallback(() => {
		if (!isDialogShown()) {
			onToggleFromShortcut();
			toggleSideNav();
		}
	}, [onToggleFromShortcut, toggleSideNav]);
	return isLiveEditMode || isOnEditRoute ? (
		<ShortcutListener accelerator={UNIVERSAL_SIDE_NAVIGATION_SHORTCUT} listener={toggle} />
	) : (
		<GeneralShortcutListener
			accelerator={[UNIVERSAL_SIDE_NAVIGATION_SHORTCUT, SIDE_NAVIGATION_SHORTCUT]}
			listener={toggle}
		/>
	);
};

const useIsModeGlobal = () => {
	const { match } = useContext(RoutesContext);

	const [spaceKey] = usePageSpaceKey();

	return match?.query?.mode === 'global' || isCompanyHubSpaceKey(spaceKey);
};

const isSpaceNavigationView = (view: string | undefined) => {
	return view === CONTAINER_HOME_ID || view === CONTAINER_BLOG_ID;
};

//todo: are all these views still relevant? I'm not certain they are
const getProductSideNavigation = (
	view: string | undefined,
	isSpaceSettingsScreen: boolean,
	isModeGlobal: boolean,
	isOnAnySettingsScreen: boolean,
	activeNav4Spotlight,
	setActiveNav4Spotlight,
	unhandledError: ApolloError | undefined,
	isLicensed: boolean,
) => {
	// hide product nav if mode=global unless we're on a settings page
	if (isModeGlobal && !isOnAnySettingsScreen) {
		return null;
	}

	// Update the utility function if logic changes for determining SpaceNavigation.
	// This ensures consistent behavior across the app, as the check is used in the footer as well.
	if (isSpaceNavigationView(view)) {
		if (view === CONTAINER_HOME_ID) {
			return (
				<SpaceNavigation
					isSpaceSettingsScreen={isSpaceSettingsScreen}
					activeNav4Spotlight={activeNav4Spotlight}
					setActiveNav4Spotlight={setActiveNav4Spotlight}
					{...(unhandledError && { unhandledError })}
				/>
			);
		}

		if (view === CONTAINER_BLOG_ID) {
			return (
				<SpaceNavigation
					isBlogNavigation
					isSpaceSettingsScreen={false}
					activeNav4Spotlight={activeNav4Spotlight}
					setActiveNav4Spotlight={setActiveNav4Spotlight}
				/>
			);
		}
	}

	switch (view) {
		case CONTAINER_HOME_LOADING_ID:
			return <SpaceNavigationLoadingSkeleton />;
		case CONTAINER_ADMIN_HOME_ID:
			return <SpaceAdminNavigation />;
		case COMPANY_HUB_SETTINGS_ID:
			return <HubSettingsNavigation />;
		case ADMIN_HOME_ID:
			return <AdminNavigationLoader />;
	}
	return isLicensed ? <StarredSpacesList /> : null;
};

export const SideNavigation = ({
	view,
	isSpaceSettingsScreen,
	activeNav4Spotlight,
	setActiveNav4Spotlight,
	isNav4CollapseReminderSpotlightActive,
	setIsNav4CollapseReminderSpotlightActive,
}: {
	view: string | undefined;
	isSpaceSettingsScreen: boolean;
	skiplinkText?: string;
	isNav4CollapseReminderSpotlightActive?: boolean;
	setIsNav4CollapseReminderSpotlightActive?: Dispatch<SetStateAction<boolean>>;
} & Partial<Nav4ChangeboardingSpotlightProps>) => {
	const intl = useIntl();
	const isModeGlobal = useIsModeGlobal(); //instead of disabling the whole left nav, now mode=Global will disable only the space Navigation
	const { initialCollapsedState, initialWidth, shouldForceExpand } = useSideNavInitialState();
	const { scrollTree } = useScrollTree({
		view,
	});
	const { saveCurrentRoute } = useSettingsBackButtonUtils();
	const [spaceKey] = usePageSpaceKey();
	const { isLicensed } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isOnEditRoute = useIsEditorPage();
	const isPageWithinSpaceSettings = view === CONTAINER_ADMIN_HOME_ID;
	const isPageWithinSiteSettings = view === ADMIN_HOME_ID;
	const isHubSettingsScreen = view === COMPANY_HUB_SETTINGS_ID;
	const isOnAnySettingsScreen =
		isPageWithinSpaceSettings || isPageWithinSiteSettings || isHubSettingsScreen;
	const { nav4GlobalChangeboardingActive, nav4SpaceSettingsChangeboardingActive } =
		useModernizeChangeboardingState();
	const expandSideNav = useExpandSideNav();

	const {
		isUserViewingSpaceAsAnon,
		loading: anonymousAccessQueryLoading,
		error: anonymousAccessQueryError,
	} = useIsUserViewingSpaceAsAnon({ spaceKey: spaceKey || '' });
	const { setIsCollapsed } = useSideNavigationCollapsedActions();
	const { isEligibleForLand, isEligibleForXflow } = useIsEligibleForEnhanceOrientationTour();

	const isSpaceNavigationRoute = isSpaceNavigationView(view);
	const showEnhanceTourExperiment =
		(isSpaceNavigationRoute &&
			!isUserViewingSpaceAsAnon &&
			isEligibleForLand &&
			expValEquals('cc-onboarding-enhance-orientation-tour-land', 'cohort', 'experiment')) ||
		(isSpaceNavigationRoute &&
			!isUserViewingSpaceAsAnon &&
			isEligibleForXflow &&
			expValEquals('cc-onboarding-enhance-orientation-tour-xflow', 'cohort', 'experiment'));

	//  Calibration event for enhance orientation tour experiment
	if (isSpaceNavigationRoute) {
		isEligibleForLand &&
			expValEquals('cc-onboarding-enhance-orientation-tour-land-aa', 'cohort', 'experiment');

		isEligibleForXflow &&
			expValEquals('cc-onboarding-enhance-orientation-tour-xflow-aa', 'cohort', 'experiment');
	}

	const productSideNavigation = getProductSideNavigation(
		view,
		isSpaceSettingsScreen,
		isModeGlobal,
		isOnAnySettingsScreen,
		activeNav4Spotlight,
		setActiveNav4Spotlight,
		anonymousAccessQueryError,
		isLicensed,
	);

	const [triggeredByShortcut, setTriggeredByShortcut] = useState(false);

	const fireSidebarToggleAnalytics = useCallback(
		(action: 'collapsed' | 'expanded', triggeredBy: 'keyboardShortcut' | 'click') => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'spaceNavigation',
					source: 'containerNavigation',
					attributes: {
						isOnEditRoute,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
						triggeredBy,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute],
	);

	const onCollapse = useCallback(() => {
		setIsCollapsed(true);
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
			'collapsed',
		);

		resize();
		fireSidebarToggleAnalytics('collapsed', triggeredByShortcut ? 'keyboardShortcut' : 'click');
		setTriggeredByShortcut(false);
	}, [fireSidebarToggleAnalytics, triggeredByShortcut, setIsCollapsed]);

	const onExpand = useCallback(() => {
		setIsCollapsed(false);
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED,
			'expanded',
		);
		scrollTree();
		resize();
		fireSidebarToggleAnalytics('expanded', triggeredByShortcut ? 'keyboardShortcut' : 'click');
		setTriggeredByShortcut(false);
	}, [scrollTree, fireSidebarToggleAnalytics, triggeredByShortcut, setIsCollapsed]);

	const handleToggleFromShortcut = useCallback(() => {
		setTriggeredByShortcut(true);
	}, []);

	// If current route is not a settings page, save the current route to session storage.
	// The saved route is read by the back button whenever the user lands in Site or Space settings in the same session.
	if (!isPageWithinSpaceSettings && !isPageWithinSiteSettings && !isHubSettingsScreen) {
		saveCurrentRoute();
	}

	const { isNav4SpaceTourDismissed } = useGetModernizeChangeboardingOnboardingState();
	const { isNav4CollapseReminderSpotlightDismissed } =
		useHasDismissedNav4CollapseReminderSpotlight();

	useEffect(() => {
		// Expand side nav when rendering certain nav4 spotlights
		if (
			(nav4GlobalChangeboardingActive || nav4SpaceSettingsChangeboardingActive) &&
			fg('cc_modernize_ga_changeboarding')
		) {
			expandSideNav();
		}
	}, [nav4GlobalChangeboardingActive, nav4SpaceSettingsChangeboardingActive, expandSideNav]);

	return (
		<SideNav
			defaultWidth={initialWidth}
			defaultCollapsed={initialCollapsedState === 'collapsed'}
			onExpand={onExpand}
			onCollapse={onCollapse}
			id="side-navigation"
		>
			<SuspenseWithDataFetcher dataFetcherName="side-navigation">
				<UFOSegment name="side-nav">
					<SideNavigationContent>
						<SpotlightTarget name="nav4-spotlight-0">
							<MenuList>
								{!isOnAnySettingsScreen && (
									<GlobalNavigation withVisibleAppShortcuts={view === PRODUCT_HOME_ID} />
								)}
							</MenuList>
							{productSideNavigation}
						</SpotlightTarget>
						{isNav4CollapseReminderSpotlightActive && setIsNav4CollapseReminderSpotlightActive && (
							<Nav4CollapseReminderSpotlightLoader
								setIsNav4CollapseReminderSpotlightActive={setIsNav4CollapseReminderSpotlightActive}
							/>
						)}
						{!shouldForceExpand && (
							<ExpandKeyboardShortcut onToggleFromShortcut={handleToggleFromShortcut} />
						)}
						<PanelSplitter
							onResizeEnd={({ finalWidth }) =>
								handleResizeEnd({
									finalWidth,
									eligibleForCollapseReminderSpotlight:
										!isNav4CollapseReminderSpotlightActive &&
										isNav4SpaceTourDismissed &&
										!isNav4CollapseReminderSpotlightDismissed &&
										fg('confluence_nav4_collapse_reminder_spotlight'),
									setIsNav4CollapseReminderSpotlightActive,
								})
							}
							onResizeStart={onResizeStart}
							label={intl.formatMessage(i18n.dragHandle)}
						/>
					</SideNavigationContent>
					{isUserViewingSpaceAsAnon ? (
						<AnonymousAccessSideNavMessage />
					) : (
						!anonymousAccessQueryLoading &&
						!anonymousAccessQueryError && (
							<SideNavFooterWrapped showEnhanceTourExperiment={showEnhanceTourExperiment} />
						)
					)}
				</UFOSegment>
				{!fg('cc_modernize_ga_changeboarding') && <CoordinatedNav4OnboardingModal />}
			</SuspenseWithDataFetcher>
		</SideNav>
	);
};

const SideNavFooterWrapped = ({ showEnhanceTourExperiment }) =>
	//  Enhance Tour Experiment has staggered rollout with Next Best Action dial down
	fg('post-office-connie-side-nav-killswitch') ? (
		<>
			{showEnhanceTourExperiment ? (
				<EnhanceOrientationTourNudgeLoader />
			) : (
				<PostOfficeConfluenceSideNavPlacement nav4 />
			)}
			<RtPaidFeatureEducationComponentLoadable />
			<SideNavFooter>
				<TransparentErrorBoundary attribution={Attribution.NAVX}>
					<SideNavFooterContent />
				</TransparentErrorBoundary>
			</SideNavFooter>
		</>
	) : (
		<SideNavFooter>
			<TransparentErrorBoundary attribution={Attribution.NAVX}>
				<SideNavFooterContent />
			</TransparentErrorBoundary>
		</SideNavFooter>
	);

const globalInviteButtonStyles = xcss({
	flexGrow: 1,
});

const Nav4SideNavFooterContent = withFlags(({ flags }: WithFlagsProps) => {
	const intl = useIntl();

	return (
		<Inline space="space.100">
			<Box xcss={globalInviteButtonStyles}>
				<GlobalInviteWithLazyLoadedRelayProvider product="confluence" showFlag={flags.showFlag}>
					{intl.formatMessage(i18n.globalInviteButtonLabel)}
				</GlobalInviteWithLazyLoadedRelayProvider>
			</Box>
			<Nav4GiveFeedbackButton iconOnly />
		</Inline>
	);
});

const SideNavFooterContent = () => {
	const { error, loading, isEligible } = usePersistentInviteButtonEligible();

	if (loading) return <Nav4GiveFeedbackButton />;

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		} else {
			return (
				<ErrorDisplay error={error}>
					<Nav4GiveFeedbackButton />
				</ErrorDisplay>
			);
		}
	}

	return isEligible && fg('confluence_nav4_global-invite') ? (
		<Nav4SideNavFooterContent />
	) : (
		<Nav4GiveFeedbackButton />
	);
};

// CCT-951 TEMPORARY WORKAROUND to allow resizing of side-nav when viewing a Whiteboard (rendered in an iFrame)
// Can be removed when DSP-5931 is fixed and @atlaskit/page-layout is bumped
const onResizeStart = () => {
	(
		document.querySelector(`iframe[title="${WHITEBOARD_IFRAME_TITLE}"]`) as HTMLElement
	)?.style.setProperty('pointer-events', 'none');
};

const SIDE_NAVIGATION_MIN_WIDTH = 240;

const handleResizeEnd = ({
	finalWidth,
	eligibleForCollapseReminderSpotlight,
	setIsNav4CollapseReminderSpotlightActive,
}: {
	finalWidth: number;
	eligibleForCollapseReminderSpotlight: boolean;
	setIsNav4CollapseReminderSpotlightActive?: Dispatch<SetStateAction<boolean>>;
}) => {
	// CCT-951 TEMPORARY WORKAROUND to allow resizing of side-nav when viewing a Whiteboard (rendered in an iFrame)
	// Can be removed when DSP-5931 is fixed and @atlaskit/page-layout is bumped
	(
		document.querySelector(`iframe[title="${WHITEBOARD_IFRAME_TITLE}"]`) as HTMLElement
	)?.style.removeProperty('pointer-events');

	confluenceLocalStorageInstance.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH,
		finalWidth,
	);

	if (finalWidth <= SIDE_NAVIGATION_MIN_WIDTH && eligibleForCollapseReminderSpotlight) {
		setIsNav4CollapseReminderSpotlightActive?.(true);
	}
};
